import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, Container, Spinner, Table } from 'react-bootstrap';
import BinanceDepositPanel from './BinanceDepositPanel';
import SiteHeader from './SiteHeader';
import { useLanguage } from '../contexts/LanguageContext';
import './Landing.css';
import { t, tf } from '../utils/i18n';

const API_BASE = process.env.REACT_APP_API_URL || '';

export default function DomainCheckout() {
  const { language } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const domainParam = (searchParams.get('domain') || '').trim();

  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [config, setConfig] = useState({ paypal: false, binanceDeposit: false });
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [payBusy, setPayBusy] = useState(false);
  const [payError, setPayError] = useState(null);
  const [binanceInfo, setBinanceInfo] = useState(null);
  const [success, setSuccess] = useState(null);
  const [pollTick, setPollTick] = useState(0);

  const langApi = language === 'en' ? 'en' : 'es';

  useEffect(() => {
    let c = false;
    fetch(`${API_BASE}/auth/me`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!c) setUser(data);
      })
      .finally(() => {
        if (!c) setAuthLoading(false);
      });
    return () => {
      c = true;
    };
  }, []);

  useEffect(() => {
    fetch(`${API_BASE}/api/checkout/domain/config`)
      .then((r) => (r.ok ? r.json() : {}))
      .then((data) => {
        setConfig({
          paypal: !!data.paypal,
          binanceDeposit: !!data.binanceDeposit
        });
      })
      .catch(() => {});
  }, []);

  const loadQuote = useCallback(async () => {
    if (!domainParam || !user) return;
    setQuoteLoading(true);
    setQuoteError(null);
    setQuote(null);
    try {
      const r = await fetch(`${API_BASE}/api/checkout/domain/quote`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: domainParam, lang: langApi })
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        if (data.error === 'price_unavailable') {
          setQuoteError('checkoutDomain.errorPriceUnavailable');
        } else if (data.error === 'domain_not_available' || (data.canRegister === false)) {
          setQuoteError('checkoutDomain.errorNotAvailable');
        } else {
          setQuoteError('checkoutDomain.errorQuote');
        }
        return;
      }
      if (!data.canRegister) {
        setQuoteError('checkoutDomain.errorNotAvailable');
        return;
      }
      setQuote(data);
    } catch {
      setQuoteError('checkoutDomain.errorQuote');
    } finally {
      setQuoteLoading(false);
    }
  }, [domainParam, user, langApi]);

  useEffect(() => {
    loadQuote();
  }, [loadQuote]);

  // PayPal return: ?dc_session=&paypal_return=1&token=ORDER_ID
  useEffect(() => {
    if (searchParams.get('paypal_return') !== '1') return;
    const orderId = searchParams.get('token');
    if (!orderId || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/checkout/domain/paypal/capture`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ orderId })
        });
        const data = await r.json().catch(() => ({}));
        if (cancelled) return;
        if (r.ok && data.status === 'captured') {
          setSuccess({ domain: data.domain, purchaseId: data.purchaseId });
          setSearchParams({}, { replace: true });
        } else {
          setPayError('checkoutDomain.errorPayPalCapture');
        }
      } catch {
        if (!cancelled) setPayError('checkoutDomain.errorPayPalCapture');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [searchParams, user, setSearchParams]);

  const startPayPal = async () => {
    if (!quote || !domainParam) return;
    setPayBusy(true);
    setPayError(null);
    try {
      const r = await fetch(`${API_BASE}/api/checkout/domain/create-order`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: quote.domain, provider: 'paypal', lang: langApi })
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data.approvalUrl) {
        setPayError('checkoutDomain.errorPayPalStart');
        setPayBusy(false);
        return;
      }
      window.location.href = data.approvalUrl;
    } catch {
      setPayError('checkoutDomain.errorPayPalStart');
      setPayBusy(false);
    }
  };

  const startBinance = async () => {
    if (!quote || !domainParam) return;
    setPayBusy(true);
    setPayError(null);
    setBinanceInfo(null);
    try {
      const r = await fetch(`${API_BASE}/api/checkout/domain/create-order`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: quote.domain, provider: 'binance_deposit', lang: langApi })
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setPayError('checkoutDomain.errorBinanceStart');
        setPayBusy(false);
        return;
      }
      setBinanceInfo(data);
      setPayBusy(false);
    } catch {
      setPayError('checkoutDomain.errorBinanceStart');
      setPayBusy(false);
    }
  };

  useEffect(() => {
    if (!binanceInfo?.reference || !user) return;
    const tmr = setInterval(() => setPollTick((x) => x + 1), 8000);
    return () => clearInterval(tmr);
  }, [binanceInfo?.reference, user]);

  useEffect(() => {
    if (!binanceInfo?.reference || !user || success) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          `${API_BASE}/api/checkout/domain/status?reference=${encodeURIComponent(binanceInfo.reference)}`,
          { credentials: 'include' }
        );
        const data = await r.json().catch(() => ({}));
        if (cancelled) return;
        if (r.ok && data.status === 'captured') {
          setSuccess({ domain: data.domain, purchaseId: data.purchaseId });
          setBinanceInfo(null);
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [binanceInfo?.reference, user, success, pollTick]);

  if (authLoading) {
    return (
      <div className="landing landing-theme-dark min-vh-100">
        <SiteHeader variant="app" />
        <div className="site-main-with-header min-vh-100 d-flex align-items-center justify-content-center">
          <Spinner animation="border" variant="light" />
        </div>
      </div>
    );
  }

  if (!user) {
    const ret = `/checkout/domain?domain=${encodeURIComponent(domainParam)}`;
    return (
      <div className="landing landing-theme-dark min-vh-100">
        <SiteHeader variant="app" />
        <Container className="site-main-with-header py-5 text-secondary" style={{ maxWidth: 560 }}>
          <Card className="checkout-shell-card shadow-none">
            <Card.Body className="p-4">
              <h1 className="h4 mb-3">{t('checkoutDomain.loginRequiredTitle', language)}</h1>
              <p className="small mb-4 opacity-75">{t('checkoutDomain.loginRequiredBody', language)}</p>
              <Button as={Link} to={`/user?return=${encodeURIComponent(ret)}`} className="landing-btn-primary border-0">
                {t('checkoutDomain.goToLogin', language)}
              </Button>
            </Card.Body>
          </Card>
        </Container>
      </div>
    );
  }

  if (!domainParam) {
    return (
      <div className="landing landing-theme-dark min-vh-100">
        <SiteHeader variant="app" />
        <Container className="site-main-with-header py-5 text-secondary">
          <Alert variant="warning" className="border-0">
            {t('checkoutDomain.missingDomain', language)}
          </Alert>
          <Link to="/" className="landing-news-all-link d-inline-flex mt-2">
            {t('checkoutDomain.backHome', language)}
          </Link>
        </Container>
      </div>
    );
  }

  if (success) {
    return (
      <div className="landing landing-theme-dark min-vh-100">
        <SiteHeader variant="app" />
        <Container className="site-main-with-header py-5 text-secondary" style={{ maxWidth: 640 }}>
          <Alert variant="success" className="border-0">
            <strong>{t('checkoutDomain.successTitle', language)}</strong>
            <p className="mb-0 mt-2 small">
              {tf('checkoutDomain.successBody', { domain: success.domain }, language)}
            </p>
          </Alert>
          <p className="small opacity-75">{t('checkoutDomain.fulfillmentNote', language)}</p>
          <Button as={Link} to="/user" className="landing-btn-primary border-0">
            {t('checkoutDomain.goToAccount', language)}
          </Button>
        </Container>
      </div>
    );
  }

  return (
    <div className="landing landing-theme-dark min-vh-100">
      <SiteHeader variant="app" />
      <Container className="site-main-with-header py-5 text-secondary" style={{ maxWidth: 720 }}>
      <div className="mb-4">
        <Link to="/" className="landing-news-all-link d-inline-flex">
          {t('checkoutDomain.backHome', language)}
        </Link>
      </div>
      <h1 className="h3 mb-3">{t('checkoutDomain.title', language)}</h1>
      <p className="text-muted small mb-4">{t('checkoutDomain.intro', language)}</p>

      {searchParams.get('cancelled') === '1' && (
        <Alert variant="secondary" className="small">
          {t('checkoutDomain.payPalCancelled', language)}
        </Alert>
      )}

      {quoteLoading && (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" className="me-2" />
          {t('checkoutDomain.loadingQuote', language)}
        </div>
      )}

      {quoteError && <Alert variant="danger">{t(quoteError, language)}</Alert>}
      {payError && <Alert variant="danger">{t(payError, language)}</Alert>}

      {quote && !quoteLoading && (
        <Card className="checkout-shell-card shadow-none mb-4">
          <Card.Body>
            <Table borderless size="sm" className="mb-0 checkout-flow-quote-table">
              <tbody>
                <tr>
                  <td className="checkout-flow-label">{t('checkoutDomain.labelDomain', language)}</td>
                  <td className="checkout-flow-value">{quote.domain}</td>
                </tr>
                <tr>
                  <td className="checkout-flow-label">{t('checkoutDomain.labelAmount', language)}</td>
                  <td className="checkout-flow-value checkout-flow-value-total">
                    {tf('checkoutDomain.amountUsd', { amount: String(quote.amountUsd) }, language)}
                  </td>
                </tr>
              </tbody>
            </Table>
            <p className="small checkout-flow-disclaimer mt-3 mb-0">{t('checkoutDomain.quoteDisclaimer', language)}</p>
          </Card.Body>
        </Card>
      )}

      {quote && !quoteLoading && (
        <div className="d-flex flex-column gap-3">
          {config.paypal && (
            <Button variant="primary" size="lg" disabled={payBusy} onClick={startPayPal}>
              {payBusy ? <Spinner size="sm" animation="border" className="me-2" /> : null}
              {t('checkoutDomain.payWithPayPal', language)}
            </Button>
          )}
          {config.binanceDeposit && (
            <Button variant="outline-dark" size="lg" disabled={payBusy} onClick={startBinance}>
              {t('checkoutDomain.payWithBinanceWallet', language)}
            </Button>
          )}
          {!config.paypal && !config.binanceDeposit && (
            <Alert variant="warning">{t('checkoutDomain.noGateways', language)}</Alert>
          )}
        </div>
      )}

      {binanceInfo && (
        <Card className="mt-4 border-warning">
          <Card.Body>
            <BinanceDepositPanel
              info={binanceInfo}
              language={language}
              translationPrefix="checkoutDomain"
            />
          </Card.Body>
        </Card>
      )}
    </Container>
    </div>
  );
}
